import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react'
import { authApi } from '@/lib/api/auth'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface FormValues {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export default function ChangePasswordPage() {
  const navigate = useNavigate()
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const user = useAuthStore((s) => s.user)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { register, handleSubmit, watch } = useForm<FormValues>({ mode: 'onChange' })
  const newPassword = watch('newPassword', '')

  const checks = [
    { label: 'At least 8 characters', ok: newPassword.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(newPassword) },
    { label: 'One number', ok: /[0-9]/.test(newPassword) },
  ]

  const onSubmit = async (values: FormValues) => {
    if (values.newPassword !== values.confirmPassword) {
      setError('New passwords do not match')
      return
    }
    setError('')
    setLoading(true)
    try {
      const res = await authApi.changePassword(values.currentPassword, values.newPassword)
      if (!res.success) throw new Error(res.error ?? 'Failed to change password')

      // After password change, all sessions are revoked — log out and re-login
      clearAuth()
      navigate('/login?reason=password_changed', { replace: true })
    } catch (err: any) {
      setError(err?.error ?? err?.message ?? 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0B2545] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight">Monarch Payments</h1>
          {user?.mustChangePassword && (
            <p className="text-amber-300 mt-1.5 text-sm">
              You must change your password before continuing
            </p>
          )}
        </div>

        <Card className="shadow-2xl border-white/10 bg-white">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Change Password</CardTitle>
            <CardDescription>
              {user?.mustChangePassword
                ? 'A new password is required to access the portal'
                : 'Update your account password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrent ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pr-10"
                    {...register('currentPassword', { required: true })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent((v) => !v)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNew ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pr-10"
                    {...register('newPassword', { required: true })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPassword.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {checks.map((c) => (
                      <li key={c.label} className={`flex items-center gap-1.5 text-xs ${c.ok ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className={`h-3 w-3 ${c.ok ? 'text-emerald-500' : 'text-muted-foreground/40'}`} />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  {...register('confirmPassword', { required: true })}
                />
              </div>

              <Button
                type="submit"
                className="w-full mt-2"
                disabled={loading || checks.some((c) => !c.ok)}
              >
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Changing…</> : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
